import React, { useState, useEffect } from 'react';
import { artistService, albumService, genreService } from '../../../services/musicService';
import uploadToDropbox from '../../../services/dropboxService';

function SongForm({ initialData, onSubmit, onCancel, title = 'Add Song' }) {
  const [formData, setFormData] = useState({
    title: '',
    artist_id: '',
    album_id: '',
    duration_seconds: '',
    genre: '',
    release_year: '',
    audio_url: '',
    cover_image_url: '',
    lyrics: ''
  });
  const [artists, setArtists] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [genres, setGenres] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState({
    audio: false,
    cover: false
  });
  const [uploadProgress, setUploadProgress] = useState({
    audio: 0,
    cover: 0
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchArtists();
    fetchGenres();
    
    if (initialData) {
      setFormData({
        title: initialData.title || '',
        artist_id: initialData.artist_id || '',
        album_id: initialData.album_id || '',
        duration_seconds: initialData.duration_seconds || '',
        genre: initialData.genre || '',
        release_year: initialData.release_year || '',
        audio_url: initialData.audio_url || '',
        cover_image_url: initialData.cover_image_url || '',
        lyrics: initialData.lyrics || ''
      });
    }
  }, [initialData]);

  useEffect(() => {
    if (formData.artist_id) {
      fetchAlbumsByArtist(formData.artist_id);
    } else {
      setAlbums([]);
    }
  }, [formData.artist_id]);

  const fetchArtists = async () => {
    try {
      const response = await artistService.getAllArtists();
      setArtists(response.data?.data || []);
    } catch (error) {
      console.error('Failed to fetch artists:', error);
    }
  };

  const fetchAlbumsByArtist = async (artistId) => {
    try {
      setLoading(true);
      const response = await albumService.getAlbumsByArtist(artistId);
      setAlbums(response.data?.data || []);
    } catch (error) {
      console.error('Failed to fetch albums:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGenres = async () => {
    try {
      const response = await genreService.getAllGenres();
      setGenres(response.data?.data || []);
    } catch (error) {
      console.error('Failed to fetch genres:', error);
    }
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    
    if (!formData.artist_id) {
      newErrors.artist_id = 'Artist is required';
    }
    
    // Duration validation: only if provided
    if (formData.duration_seconds) {
      const duration = parseInt(formData.duration_seconds);
      if (isNaN(duration) || duration < 1) {
        newErrors.duration_seconds = 'Duration must be a positive number';
      }
    }
    
    if (!formData.audio_url.trim()) {
      newErrors.audio_url = 'Audio file is required';
    }
    
    // Release year validation: only if provided
    if (formData.release_year) {
      const year = parseInt(formData.release_year);
      const currentYear = new Date().getFullYear();
      if (year < 1900 || year > currentYear) {
        newErrors.release_year = `Year must be between 1900 and ${currentYear}`;
      }
    }
    
    return newErrors;
  };

  const handleFileUpload = async (file, type) => {
    if (!file) return;
    
    if (type === 'audio') {
      const validAudioTypes = ['audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/mp3', 'audio/mp4', 'audio/x-m4a'];
      if (!validAudioTypes.includes(file.type)) {
        setErrors(prev => ({ ...prev, audio_url: 'Please select a valid audio file (MP3, WAV, OGG, M4A)' }));
        return;
      }
      
      const maxSize = 50 * 1024 * 1024;
      if (file.size > maxSize) {
        setErrors(prev => ({ ...prev, audio_url: 'Audio file size must be less than 50MB' }));
        return;
      }
    } else if (type === 'cover') {
      const validImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/jpg'];
      if (!validImageTypes.includes(file.type)) {
        setErrors(prev => ({ ...prev, cover_image_url: 'Please select a valid image file (JPEG, PNG, GIF, WEBP)' }));
        return;
      }
      
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        setErrors(prev => ({ ...prev, cover_image_url: 'Image size must be less than 5MB' }));
        return;
      }
    }
    
    setUploading(prev => ({ ...prev, [type]: true }));
    setUploadProgress(prev => ({ ...prev, [type]: 0 }));
    
    try {
      const downloadUrl = await uploadToDropbox(file, (progress) => {
        setUploadProgress(prev => ({ ...prev, [type]: progress }));
      });
      
      if (type === 'audio') {
        setFormData(prev => ({ ...prev, audio_url: downloadUrl }));
        setErrors(prev => ({ ...prev, audio_url: '' }));
      } else if (type === 'cover') {
        setFormData(prev => ({ ...prev, cover_image_url: downloadUrl }));
        setErrors(prev => ({ ...prev, cover_image_url: '' }));
      }
    } catch (error) {
      console.error('Upload failed:', error);
      setErrors(prev => ({ 
        ...prev, 
        [type === 'audio' ? 'audio_url' : 'cover_image_url']: 'Failed to upload file. Please try again.' 
      }));
    } finally {
      setUploading(prev => ({ ...prev, [type]: false }));
      setUploadProgress(prev => ({ ...prev, [type]: 0 }));
    }
  };

  const handleFileSelect = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      handleFileUpload(file, type);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
  
    const submitData = {
      ...formData,
      album_id: formData.album_id || null,
      release_year: formData.release_year || null,
      genre: formData.genre || null,
      lyrics: formData.lyrics || null,
      cover_image_url: formData.cover_image_url || null,
      duration_seconds: formData.duration_seconds ? parseInt(formData.duration_seconds) : null
    };
    
    onSubmit(submitData);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const parseDuration = (durationString) => {
    if (!durationString) return 0;
    const [mins, secs] = durationString.split(':').map(Number);
    return (mins * 60) + (secs || 0);
  };

  const handleDurationChange = (e) => {
    const value = e.target.value;
    if (!value) {
      setFormData(prev => ({ ...prev, duration_seconds: '' }));
    } else {
      setFormData(prev => ({ ...prev, duration_seconds: parseDuration(value) }));
    }
    if (errors.duration_seconds) {
      setErrors(prev => ({ ...prev, duration_seconds: '' }));
    }
  };

  const currentYear = new Date().getFullYear();

  return (
    <div className="bg-gray-900/30 backdrop-blur-sm border border-gray-800 rounded-2xl p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-white">{title}</h2>
        <button
          onClick={onCancel}
          className="text-gray-400 hover:text-white"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      
      <form onSubmit={handleSubmit}>
        <div className="space-y-4">
          <div>
            <label className="block text-gray-300 mb-2">Title *</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className={`w-full px-4 py-3 bg-gray-800/50 border ${errors.title ? 'border-red-500' : 'border-gray-700'} rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500`}
              placeholder="Enter song title"
            />
            {errors.title && (
              <p className="mt-1 text-red-400 text-sm">{errors.title}</p>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-300 mb-2">Artist *</label>
              <select
                name="artist_id"
                value={formData.artist_id}
                onChange={handleChange}
                className={`w-full px-4 py-3 bg-gray-800/50 border ${errors.artist_id ? 'border-red-500' : 'border-gray-700'} rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500`}
              >
                <option value="">Select an artist</option>
                {artists.map(artist => (
                  <option key={artist.artist_id || artist.id} value={artist.artist_id || artist.id}>
                    {artist.name}
                  </option>
                ))}
              </select>
              {errors.artist_id && (
                <p className="mt-1 text-red-400 text-sm">{errors.artist_id}</p>
              )}
            </div>
            
            <div>
              <label className="block text-gray-300 mb-2">Album</label>
              <select
                name="album_id"
                value={formData.album_id}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
                disabled={loading || !formData.artist_id}
              >
                <option value="">Select an album (optional)</option>
                {albums.map(album => (
                  <option key={album.album_id || album.id} value={album.album_id || album.id}>
                    {album.title}
                  </option>
                ))}
              </select>
              {loading && formData.artist_id && (
                <p className="mt-1 text-gray-500 text-sm">Loading albums...</p>
              )}
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-gray-300 mb-2">Duration (mm:ss)</label>
              <input
                type="text"
                value={formData.duration_seconds ? formatDuration(formData.duration_seconds) : ''}
                onChange={handleDurationChange}
                className={`w-full px-4 py-3 bg-gray-800/50 border ${errors.duration_seconds ? 'border-red-500' : 'border-gray-700'} rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500`}
                placeholder="mm:ss (optional)"
              />
              {errors.duration_seconds && (
                <p className="mt-1 text-red-400 text-sm">{errors.duration_seconds}</p>
              )}
            </div>
            
            <div>
              <label className="block text-gray-300 mb-2">Genre</label>
              <select
                name="genre"
                value={formData.genre}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
              >
                <option value="">Select genre</option>
                <option value="rock">Rock</option>
                <option value="pop">Pop</option>
                <option value="jazz">Jazz</option>
                <option value="hiphop">Hip Hop</option>
                <option value="electronic">Electronic</option>
                <option value="classical">Classical</option>
                <option value="country">Country</option>
                <option value="r&b">R&B</option>
                <option value="metal">Metal</option>
                <option value="folk">Folk</option>
              </select>
            </div>
            
            <div>
              <label className="block text-gray-300 mb-2">Release Year</label>
              <select
                name="release_year"
                value={formData.release_year}
                onChange={handleChange}
                className={`w-full px-4 py-3 bg-gray-800/50 border ${errors.release_year ? 'border-red-500' : 'border-gray-700'} rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500`}
              >
                <option value="">Select year (optional)</option>
                {Array.from({ length: currentYear - 1899 }, (_, i) => currentYear - i).map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
              {errors.release_year && (
                <p className="mt-1 text-red-400 text-sm">{errors.release_year}</p>
              )}
            </div>
          </div>
          
          {/* Audio File Upload Section */}
          <div>
            <label className="block text-gray-300 mb-2">Audio File *</label>
            <div className="space-y-3">
              <div className="flex items-center gap-4">
                <input
                  type="file"
                  accept="audio/*"
                  onChange={(e) => handleFileSelect(e, 'audio')}
                  disabled={uploading.audio}
                  className="hidden"
                  id="audio-upload"
                />
                <label
                  htmlFor="audio-upload"
                  className={`px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-xl transition-all font-medium cursor-pointer ${uploading.audio ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {uploading.audio ? 'Uploading...' : 'Choose Audio File'}
                </label>
                
                {uploading.audio && (
                  <div className="flex-1">
                    <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-purple-600 transition-all duration-300"
                        style={{ width: `${uploadProgress.audio * 100}%` }}
                      />
                    </div>
                    <p className="text-sm text-gray-400 mt-1">
                      {Math.round(uploadProgress.audio * 100)}% uploaded
                    </p>
                  </div>
                )}
              </div>
              
              {formData.audio_url && !uploading.audio && (
                <div className="flex items-center gap-2 text-sm bg-gray-800/30 p-3 rounded-xl">
                  <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-green-400 flex-1">Audio file uploaded successfully!</span>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, audio_url: '' }))}
                    className="text-red-400 hover:text-red-300 ml-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              )}
              
              {errors.audio_url && (
                <p className="text-red-400 text-sm">{errors.audio_url}</p>
              )}
            </div>
          </div>
          
          {/* Cover Image Upload Section */}
          <div>
            <label className="block text-gray-300 mb-2">Cover Image</label>
            <div className="space-y-3">
              <div className="flex items-center gap-4">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileSelect(e, 'cover')}
                  disabled={uploading.cover}
                  className="hidden"
                  id="cover-upload"
                />
                <label
                  htmlFor="cover-upload"
                  className={`px-6 py-3 bg-gray-800/50 hover:bg-gray-700/50 text-white rounded-xl transition-all font-medium cursor-pointer border border-gray-700 ${uploading.cover ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {uploading.cover ? 'Uploading...' : 'Choose Cover Image'}
                </label>
                
                {uploading.cover && (
                  <div className="flex-1">
                    <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-purple-600 transition-all duration-300"
                        style={{ width: `${uploadProgress.cover * 100}%` }}
                      />
                    </div>
                    <p className="text-sm text-gray-400 mt-1">
                      {Math.round(uploadProgress.cover * 100)}% uploaded
                    </p>
                  </div>
                )}
              </div>
              
              {/* Cover Image Preview */}
              {formData.cover_image_url && !uploading.cover && (
                <div className="flex items-center gap-4 bg-gray-800/30 p-3 rounded-xl">
                  <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-700">
                    <img 
                      src={formData.cover_image_url} 
                      alt="Cover preview" 
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://via.placeholder.com/64?text=Error';
                      }}
                    />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-green-400">Cover image uploaded!</span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, cover_image_url: '' }))}
                    className="text-red-400 hover:text-red-300"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              )}
              
              {errors.cover_image_url && (
                <p className="text-red-400 text-sm">{errors.cover_image_url}</p>
              )}
              
              <p className="text-gray-500 text-xs">
                Recommended: Square image, at least 300x300px. Max size: 5MB (JPEG, PNG, GIF, WEBP)
              </p>
            </div>
          </div>
          
          <div>
            <label className="block text-gray-300 mb-2">Lyrics</label>
            <textarea
              name="lyrics"
              value={formData.lyrics}
              onChange={handleChange}
              rows="6"
              className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 resize-none"
              placeholder="Enter song lyrics (optional)"
            />
          </div>
        </div>
        
        <div className="flex gap-3 mt-8">
          <button
            type="submit"
            disabled={uploading.audio || uploading.cover}
            className={`flex-1 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-xl transition-all font-medium ${(uploading.audio || uploading.cover) ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {initialData ? 'Update Song' : 'Create Song'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 px-6 py-3 bg-gray-800/50 hover:bg-gray-700/50 text-gray-300 rounded-xl transition-all border border-gray-700"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export default SongForm;